import { LogKey, logGroupsRaw } from 'pages/Logs/Logs';
import {
  selectLogSearch,
  selectLogSearchOptions,
  selectLogSearches,
  selectLogs,
  selectLogsChecked,
  selectSingleLogTime,
  setLogsChecked,
  setSingleLogTime,
} from 'pages/Logs/redux';
import { selectHotelData, selectMetricTime } from 'pages/redux';
import { useHistory, useLocation } from 'react-router-dom';
import { HOSPITALITY_PATHS } from 'pages/Layout/hospitalityModulePaths';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { aggregateLogsToMetricLogs } from './aggregateMetricLogs';
import {
  MetricLog, selectMetricChecked, selectMetricLogs, selectMetricsFlowType
} from './redux';
import { useCallback, useMemo } from 'react';
import {
  buildInventoryGroupAudits,
  buildInventoryItemAudits,
  departmentInventoryAuditsChecked,
  departmentsPresentInLogs,
  filterLogsByAudits,
} from 'pages/Logs/logUtils';
import {
  collectLogSearchQueries,
  filterLogsBySearch,
} from 'pages/Logs/logSearch';
import { buildStaffMaps } from 'pages/Logs/logActor';
import { useSettings } from 'pages/Settings/hooks';
import { useAuditsDisplayLogs } from 'pages/Logs/useAuditsDisplayLogs';

export const metrics = {
  Balances: ['Active audits', 'Hospitality', 'Departments', 'Inventory', 'All balances'] as const,
  Counts: ['Active audits2', 'Walk-ins', 'Users', 'All audits'] as const // 'Online visitor'
};

export type MetricsKey = typeof metrics['Balances'][number] | typeof metrics['Counts'][number];

export const useMetrics = () => {
  const checked = useAppSelector(selectMetricChecked);
  const logsChecked = useAppSelector(selectLogsChecked);
  const logSearch = useAppSelector(selectLogSearch);
  const logSearchOptions = useAppSelector(selectLogSearchOptions);
  const logSearches = useAppSelector(selectLogSearches);
  const metricTime = useAppSelector(selectMetricTime);
  const flowType = useAppSelector(selectMetricsFlowType);
  const metricLogs = useAppSelector(selectMetricLogs);
  const rawLogs = useAppSelector(selectLogs);
  const history = useHistory();
  const dispatch = useAppDispatch();
  const hotelData = useAppSelector(selectHotelData);
  const location = useLocation();
  const { users } = useSettings();
  const staffMaps = useMemo(() => buildStaffMaps(users), [users]);
  const searchQueries = useMemo(
    () => collectLogSearchQueries(logSearches, logSearch, logSearchOptions),
    [logSearches, logSearch, logSearchOptions],
  );
  const hasActiveSearch = searchQueries.length > 0;

  const singleLogTime = useAppSelector(selectSingleLogTime);
  const auditsTimeRangeOn = Boolean(
    singleLogTime?.startDate && singleLogTime?.endDate,
  );
  const { startDate: metricStart, endDate: metricEnd } = metricTime || {};
  const rangeStart = auditsTimeRangeOn ? singleLogTime!.startDate : metricStart;
  const rangeEnd = auditsTimeRangeOn ? singleLogTime!.endDate : metricEnd;

  // When /audits time range is on, reuse the same filtered log set as the Audits table.
  const {
    displayLogs: auditsCriteriaLogsAll,
  } = useAuditsDisplayLogs({
    useTimeRange: auditsTimeRangeOn,
  });
  const activeAuditsMetricLogsAll = useMemo(
    () => aggregateLogsToMetricLogs(auditsCriteriaLogsAll),
    [auditsCriteriaLogsAll],
  );

  const inMetricTimeRange = useCallback((date: string) => (
    (!rangeStart || !rangeEnd)
    || (+new Date(date) >= +new Date(rangeStart) && +new Date(date) <= +new Date(rangeEnd))
  ), [rangeStart, rangeEnd]);

  const activeAuditsMetricLogs = useMemo(() => {
    if (auditsTimeRangeOn) return activeAuditsMetricLogsAll;
    return activeAuditsMetricLogsAll.filter((log) => inMetricTimeRange(log.date));
  }, [auditsTimeRangeOn, activeAuditsMetricLogsAll, inMetricTimeRange]);

  const { branches } = hotelData || {};
  const logGroups: Record<LogKey, string[]> = {
    ...logGroupsRaw,
    Hospitality: [...logGroupsRaw.Hospitality],
    Departments: [...logGroupsRaw.Departments],
    'Department entries': [...logGroupsRaw['Department entries']],
    Inventory: [...logGroupsRaw.Inventory],
    'Inventory Group': [...logGroupsRaw['Inventory Group']],
    Actions: [...logGroupsRaw.Actions],
  };
  const activeBranches = (branches || []).filter((b) => !b.isDeleted).map((b) => b.name);
  const allBranchNames = (branches || []).map((b) => b.name);
  const departmentsInLogs = new Set(departmentsPresentInLogs(metricLogs, allBranchNames));
  const deletedWithLogs = (branches || [])
    .filter((b) => b.isDeleted)
    .map((b) => b.name)
    .filter((name) => departmentsInLogs.has(name));
  logGroups.Departments = [...activeBranches, ...deletedWithLogs, ...logGroups.Departments];
  logGroups['Inventory Group'] = buildInventoryGroupAudits({
    inventoryTypes: hotelData?.prefs?.inventoryTypes,
    inventory: hotelData?.inventory,
    logs: metricLogs,
  });
  logGroups.Inventory = buildInventoryItemAudits({
    inventory: hotelData?.inventory,
    logs: metricLogs,
    inventoryGroupNames: logGroups['Inventory Group'],
  });
  const activeInventoryItemNames = new Set(
    (hotelData?.inventory || [])
      .map((item) => item?.name?.trim().toLowerCase())
      .filter(Boolean) as string[],
  );
  const activeInventoryGroupNames = new Set<string>();
  (hotelData?.prefs?.inventoryTypes || []).forEach((t) => {
    if (t?.trim()) activeInventoryGroupNames.add(t.trim().toLowerCase());
  });
  (hotelData?.inventory || []).forEach((item) => {
    if (item?.type?.trim()) activeInventoryGroupNames.add(item.type.trim().toLowerCase());
  });
  const deletedInventoryItemNames = new Set(
    logGroups.Inventory.filter((name) => !activeInventoryItemNames.has(name.toLowerCase())),
  );
  const deletedInventoryGroupNames = new Set(
    logGroups['Inventory Group'].filter(
      (name) => !activeInventoryGroupNames.has(name.toLowerCase()),
    ),
  );
  const searchArchives = {
    deletedTypes: deletedInventoryItemNames,
    deletedGroups: deletedInventoryGroupNames,
    deletedDepartments: new Set(deletedWithLogs),
  };
  const departmentNames = new Set(logGroups.Departments || []);
  const allAudits: string[] = [...Object.entries(logGroups).map(([k]) => k), 'Show all'];
  Object.entries(logGroups).forEach(([, v]) => allAudits.push(...v));
  const dateFilteredLogs = metricLogs.filter((log) => inMetricTimeRange(log.date));

  /**
   * Active audits: apply AuditsSelector search on raw /logs rows (same as LogTable),
   * then re-aggregate into metric buckets so TOTAL/count match the audits table.
   */
  const searchFilteredDateLogs = useMemo(() => {
    if (!hasActiveSearch) return dateFilteredLogs;
    const rawInRange = (rawLogs || []).filter((log) => inMetricTimeRange(log.date));
    const searched = filterLogsBySearch(
      rawInRange,
      searchQueries,
      staffMaps,
      searchArchives,
      departmentNames,
    );
    return aggregateLogsToMetricLogs(searched);
  }, [
    hasActiveSearch,
    dateFilteredLogs,
    rawLogs,
    inMetricTimeRange,
    searchQueries,
    staffMaps,
    // Archive sets are rebuilt each render from hotel/log state already in deps above.
    logGroups.Inventory,
    logGroups['Inventory Group'],
    logGroups.Departments,
    deletedWithLogs,
  ]);

  const searchFilteredMetricLogs = useMemo(() => {
    if (!hasActiveSearch) return metricLogs;
    const searched = filterLogsBySearch(
      rawLogs || [],
      searchQueries,
      staffMaps,
      searchArchives,
      departmentNames,
    );
    return aggregateLogsToMetricLogs(searched);
  }, [
    hasActiveSearch,
    metricLogs,
    rawLogs,
    searchQueries,
    staffMaps,
    logGroups.Inventory,
    logGroups['Inventory Group'],
    logGroups.Departments,
    deletedWithLogs,
  ]);

  const getTotal = (logsChecked1: string[], useSearch = false) => {
    const pool = useSearch ? searchFilteredDateLogs : dateFilteredLogs;
    const matched = filterLogsByAudits(pool, logsChecked1);
    return matched.reduce((a, b) => {
      const t = flowType === '-' && Number(b.value) < 0 ? Number(b.value) :
        flowType === '+' && Number(b.value) > 0 ? Number(b.value) :
          flowType === 'both' ? Number(b.value) || 0 : 0;
      return a + t;
    }, 0);
  };

  /** Active audits totals use the shared /audits display pipeline. */
  const getActiveAuditsTotal = () => activeAuditsMetricLogs.reduce((a, b) => {
    const t = flowType === '-' && Number(b.value) < 0 ? Number(b.value) :
      flowType === '+' && Number(b.value) > 0 ? Number(b.value) :
        flowType === 'both' ? Number(b.value) || 0 : 0;
    return a + t;
  }, 0);

  const getCount = (logsChecked1: string[], useSearch = false) => {
    const pool = useSearch ? searchFilteredDateLogs : dateFilteredLogs;
    const matched = filterLogsByAudits(pool, logsChecked1);
    return matched.reduce((a, b) => a + (b.count || 0), 0);
  };

  const getActiveAuditsCount = () => activeAuditsMetricLogs.reduce(
    (a, b) => a + (b.count || 0),
    0,
  );

  type TimeMetric = {
    total: () => number,
    checked: string[],
    onClick: Function,
    name: MetricsKey,
  }

  const onClickViewMetricAudits = () => {
    if (rangeStart && rangeEnd) {
      dispatch(setSingleLogTime({
        startDate: new Date(rangeStart),
        endDate: new Date(rangeEnd),
      }));
    }
    flowType === '-' ? history.push(HOSPITALITY_PATHS.auditsAction('svg1ere2xxyv')) :
      flowType === '+' ? history.push(HOSPITALITY_PATHS.auditsAction('svg1ere1xxyv')) :
        flowType === 'both' ? history.push(HOSPITALITY_PATHS.auditsAction('svg1ere3xxyv')) :
          history.push(HOSPITALITY_PATHS.auditsAction('svg1ere4xxyv'));
  };

  const allBalances = [
    ...logGroups.Departments,
    ...logGroups.Hospitality,
    ...logGroups.Inventory,
    ...logGroups['Inventory Group'],
    'Room charge',
    'Hospitality',
    'Departments',
    'Department entries',
    'Direct',
    'Expense',
    'Document',
    'Inventory',
    'Inventory Group',
  ];
  const activeAuditsBalances = logsChecked.filter((c) => allBalances.includes(c));

  const timeMetrics: Record<MetricsKey, TimeMetric> = {
    'Active audits': {
      name: 'Active audits',
      total: () => getActiveAuditsTotal(),
      checked: activeAuditsBalances,
      onClick: () => {
        onClickViewMetricAudits();
      }
    },
    Hospitality: {
      name: 'Hospitality',
      total: () => getTotal(logGroups.Hospitality),
      checked: logGroups.Hospitality,
      onClick: () => {
        dispatch(setLogsChecked(['Hospitality', ...logGroups.Hospitality]));
        onClickViewMetricAudits();
      }
    },
    Departments: {
      name: 'Departments',
      // Inventory by department, plus Direct / Expense / Document among them.
      total: () => getTotal([
        ...logGroups.Departments,
        ...logGroups['Department entries'],
      ]),
      checked: [
        ...logGroups.Departments,
        ...logGroups['Department entries'],
      ],
      onClick: () => {
        // Charts still OR-match inventory by department; /audits needs
        // department + inventory (and department entries) selected together.
        dispatch(setLogsChecked(departmentInventoryAuditsChecked(
          logGroups.Departments,
          logGroups,
        )));
        if (rangeStart && rangeEnd) {
          dispatch(setSingleLogTime({
            startDate: new Date(rangeStart),
            endDate: new Date(rangeEnd),
          }));
        }
        history.push(HOSPITALITY_PATHS.auditsAction('svg1ere1xxyv'));
      }
    },
    Inventory: {
      name: 'Inventory',
      total: () => getTotal([...logGroups.Inventory]),
      checked: [...logGroups.Inventory],
      onClick: () => {
        // Inventory shows with departments off; all selected item logs appear.
        dispatch(setLogsChecked(['Inventory', ...logGroups.Inventory]));
        onClickViewMetricAudits();
      }
    },
    'All balances': {
      name: 'All balances',
      total: () => getTotal(allBalances),
      checked: allBalances,
      onClick: () => {
        dispatch(setLogsChecked(allBalances));
        onClickViewMetricAudits();
      }
    },
    'Active audits2': {
      name: 'Active audits2',
      total: () => getActiveAuditsCount(),
      checked: logsChecked,
      onClick: () => {
        onClickViewMetricAudits();
      }
    },
    // 'Online visitor': {
    //   name: 'Online visitor',
    //   total: getCount(['Online visitor']),
    //   checked: ['Online visitor'],
    //   onClick: () => {
    //     dispatch(setLogsChecked(['Online visitor']));
    //     onClickViewMetricAudits();
    //   }
    // },
    'Walk-ins': {
      name: 'Walk-ins',
      total: () => getCount(['Walk in', 'Walk out']),
      checked: ['Walk in', 'Walk out'],
      onClick: () => {
        dispatch(setLogsChecked(['Walk in', 'Walk out']));
        onClickViewMetricAudits();
      }
    },
    Users: {
      name: 'Users',
      total: () => getCount(allAudits),
      checked: allAudits,
      onClick: () => {
        dispatch(setLogsChecked(allAudits));
        onClickViewMetricAudits();
      }
    },
    'All audits': {
      name: 'All audits',
      total: () => getCount(allAudits),
      checked: allAudits,
      onClick: () => {
        dispatch(setLogsChecked(allAudits));
        onClickViewMetricAudits();
      }
    },
  };

  const checkedMetric = timeMetrics[checked as MetricsKey] || timeMetrics['All audits'];
  const isActiveAuditsMetric = checked === 'Active audits' || checked === 'Active audits2';
  const isUsersMetric = checked === 'Users';

  const isDashBoard = location.pathname.includes('dashboard');

  // Users charts need created_by kept; default metric buckets merge staff together.
  const userMetricLogs = useMemo(() => {
    if (!isUsersMetric) return metricLogs;
    return aggregateLogsToMetricLogs(rawLogs || [], { byActor: true });
  }, [isUsersMetric, metricLogs, rawLogs]);

  const userDateFilteredLogs = useMemo(() => {
    if (!isUsersMetric) return dateFilteredLogs;
    return userMetricLogs.filter((log) => inMetricTimeRange(log.date));
  }, [isUsersMetric, dateFilteredLogs, userMetricLogs, inMetricTimeRange]);

  const metricLogsForCharts = isActiveAuditsMetric ? activeAuditsMetricLogs
    : isUsersMetric ? userMetricLogs : metricLogs;
  const dateLogsForCharts = isActiveAuditsMetric ? activeAuditsMetricLogs
    : isUsersMetric ? userDateFilteredLogs : dateFilteredLogs;

  const sortedLogs = (isActiveAuditsMetric
    ? activeAuditsMetricLogs
    : filterLogsByAudits(metricLogsForCharts, checkedMetric.checked)
  ).filter((log) => !rangeEnd
      || (rangeEnd && +new Date(log.date) <= +(rangeEnd || 0)))
    .sort((a, b) => +new Date(b.date) - +new Date(a.date));
  const maxLog = sortedLogs[1200] || [...sortedLogs].pop();

  const checkFilteredLogs = (isActiveAuditsMetric
    ? activeAuditsMetricLogs
    : filterLogsByAudits(dateLogsForCharts, checkedMetric.checked)
  )
    .filter((l) => (isDashBoard
      || +new Date(l.date) >= +new Date(maxLog?.date || 0))).sort((a, b) => +new Date(a.date)
      - +new Date(b.date));

  // Not use in dashboard(below)
  const metricTimeLogs = sortedLogs.filter((log) => !maxLog.date
    || +new Date(log.date) >= +new Date(maxLog.date))
    .sort((a, b) => +new Date(a.date) - +new Date(b.date));

  const getFlowTypeValue = (log: MetricLog) => {
    const { value, count } = log;

    return flowType === '+' ? (Number(value) || 0) > 0 ? Number(value) : 0 :
      flowType === '-' ? (Number(value) || 0) < 0 ? Number(value) : 0 :
        flowType === 'both' ? (Number(value) || 0) : count;
  };

  // eslint-disable-next-line no-unused-vars
  const getMetricValue = useCallback((criterea: (log: MetricLog) => boolean, logToUse?: string) => {
    const l = logToUse === 'logs' ? metricLogs : checkFilteredLogs;
    return (l || []).reduce((a, b) => {
      return a + (criterea(b) ? getFlowTypeValue(b) : 0);
    }, 0);
  }, [checkFilteredLogs, metricLogs, flowType]);

  return {
    logGroups,
    addBranches: activeBranches,
    timeMetrics,
    checked,
    checkedMetric,
    dateFilteredLogs,
    checkFilteredLogs,
    /** Untimed Active-audits set (day navigation). */
    activeAuditsMetricLogsAll,
    metricTimeLogs,
    metricTime,
    getMetricValue,
    getFlowTypeValue,
    getCount,
    allBalances,
    flowType,
    allAudits,
    metricChecked: checked,
    metricLogs,
    staffMaps,
  };
};

export const useMetricLogs = () => {
  return {};
};
